import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ScrollToTop from '../components/ScrollToTop';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Send,
  FileText,
  Building2,
  Loader2,
  CheckCircle2,
  XCircle,
  Copy
} from 'lucide-react';
import { pilgrimageAPI } from '@/services/api';
import { contactInfo } from '../data/mockData';
import {
  isPilgrimageRegistrationClosed,
  PILGRIMAGE_REGISTRATION_DEADLINE_FULL,
  PILGRIMAGE_REGISTRATION_DEADLINE_LABEL,
} from '../constants/pilgrimageDeadline';

const IsraelTourApplicationPage = () => {
  const { rwanda, burundi } = contactInfo.offices;
  const navigate = useNavigate();
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    passportNumber: '',
    passportIssueDate: '',
    passportExpiryDate: '',
    placeOfBirth: '',
    
    // Contact Information
    email: '',
    phone: '',
    alternatePhone: '',
    address: '',
    city: '',
    country: '',
    
    // Church Information
    churchName: '',
    churchAddress: '',
    pastorName: '',
    pastorPhone: '',
    membershipYears: '',
    
    // Travel Information
    previousTravel: '',
    medicalConditions: '',
    dietaryRequirements: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    
    // Additional Information
    specialRequests: '',
    howDidYouHear: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [registrationClosed, setRegistrationClosed] = useState(isPilgrimageRegistrationClosed());

  useEffect(() => {
    const checkDeadline = () => {
      setRegistrationClosed(isPilgrimageRegistrationClosed());
    };

    checkDeadline();
    const interval = setInterval(checkDeadline, 1000);
    return () => clearInterval(interval);
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'fullName', 'dateOfBirth', 'gender', 'nationality', 'passportNumber',
      'passportExpiryDate', 'email', 'phone', 'address', 'city', 'country',
      'emergencyContactName', 'emergencyContactPhone'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (formData.phone && !/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (registrationClosed) {
      setError(`Registration is closed. The deadline was ${PILGRIMAGE_REGISTRATION_DEADLINE_FULL}.`);
      return;
    }
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Prepare booking data for API
      const bookingData = {
        customer: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          dateOfBirth: formData.dateOfBirth || null,
          gender: formData.gender || null,
          nationality: formData.nationality || null,
          passportNumber: formData.passportNumber || null,
          passportExpiryDate: formData.passportExpiryDate || null,
          address: formData.address || null,
          city: formData.city || null,
          country: formData.country || null,
          alternatePhone: formData.alternatePhone || null,
        },
        booking: {
          tourDates: "October 6, 2026 to October 14, 2026",
          tourCost: "USD $2,900",
          churchName: formData.churchName || null,
          churchAddress: formData.churchAddress || null,
          pastorName: formData.pastorName || null,
          pastorPhone: formData.pastorPhone || null,
          membershipYears: formData.membershipYears || null,
          previousTravel: formData.previousTravel || null,
          medicalConditions: formData.medicalConditions || null,
          dietaryRequirements: formData.dietaryRequirements || null,
          emergencyContactName: formData.emergencyContactName || null,
          emergencyContactPhone: formData.emergencyContactPhone || null,
          emergencyContactRelationship: formData.emergencyContactRelationship || null,
          specialRequests: formData.specialRequests || null,
          howDidYouHear: formData.howDidYouHear || null,
        }
      };

      // Submit to backend
      const result = await pilgrimageAPI.createPilgrimageBooking(bookingData);
      
      setBookingId(result.id);
      setSubmitted(true);
      
      // Scroll to top
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      
    } catch (err) {
      console.error('Error submitting pilgrimage booking:', err);
      // FastAPI returns { detail: "msg" } or { detail: [{ loc: [], msg: "..." }] } for 422
      let errorMessage = 'Failed to submit booking. Please try again.';
      if (err?.detail) {
        errorMessage = Array.isArray(err.detail) 
          ? err.detail.map(d => d?.msg || JSON.stringify(d)).join('. ') 
          : String(err.detail);
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-brand-sky-50 to-brand-gold-50 py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
            Holy Land Pilgrimage Application
          </h1>
          <p className="text-gray-600 text-lg">
            October 6, 2026 to October 14, 2026
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Registration Deadline: {PILGRIMAGE_REGISTRATION_DEADLINE_LABEL}
          </p>
        </div>
      </section>

      {/* Success Message */}
      {submitted && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-brand-gold-50 border border-brand-gold-200 rounded-lg p-8">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="text-brand-gold-600" size={28} />
                <h3 className="text-2xl font-semibold text-brand-gold-800">✅ Booking Submitted Successfully</h3>
              </div>
              
              <div className="space-y-4 text-brand-gold-800">
                <p className="text-lg">
                  Thank you for registering for the Israel Pilgrimage Travel Program.
                </p>
                <p>
                  Your booking has been successfully submitted and is currently under review.
                  Our team will verify your details and contact you shortly with the next steps.
                </p>

                <div className="mt-6 pt-6 border-t border-brand-gold-300">
                  <h4 className="text-xl font-bold text-brand-gold-900 mb-4">💳 Payment Options</h4>
                  <p className="mb-4">You may complete your payment using any of the options below:</p>
                  
                  <div className="bg-white rounded-lg p-5 mb-4 border-2 border-brand-gold-300">
                    <h5 className="font-bold text-brand-gold-900 mb-4 text-lg">Option 1: Bank Transfer (USD)</h5>
                    <div className="space-y-3 text-sm bg-brand-blue-50 rounded-lg p-5 mb-4 border border-brand-blue-200">
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Account Name:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">Cardx Academia & Travel Tours Ltd</span>
                          <button
                            onClick={() => copyToClipboard('Cardx Academia & Travel Tours Ltd')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Account Number:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-gray-800 font-bold text-lg">4002212860747</span>
                          <button
                            onClick={() => copyToClipboard('4002212860747')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="font-bold text-gray-700">Currency:</span>
                        <span className="text-gray-800 font-semibold">USD (US Dollars)</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 bg-brand-gold-50 p-3 rounded border border-brand-gold-200">
                      <span className="font-semibold">Payment Reference:</span> Your Full Name - Israel Pilgrimage
                    </p>
                    <p className="mt-3 text-sm text-gray-700">
                      After payment, please email your proof of payment to<br />
                      <a href="mailto:info@cardxacademia.com" className="text-brand-blue-600 hover:underline font-semibold">
                        📧 info@cardxacademia.com
                      </a>
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-5 mb-4 border-2 border-brand-gold-300">
                    <h5 className="font-bold text-brand-gold-900 mb-4 text-lg">Option 2: Bank Transfer (Burundi, Eco Bank)</h5>
                    <p className="text-sm text-gray-600 mb-4">
                      For applicants paying from Burundi, you may transfer to either account below:
                    </p>

                    <div className="space-y-3 text-sm bg-brand-blue-50 rounded-lg p-5 mb-4 border border-brand-blue-200">
                      <p className="font-bold text-brand-gold-900 mb-2">BIF Account</p>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Account Name:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">Cardx Academia & Travel Tours Ltd</span>
                          <button
                            onClick={() => copyToClipboard('Cardx Academia & Travel Tours Ltd')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Account Number:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-gray-800 font-bold text-lg">38125038402</span>
                          <button
                            onClick={() => copyToClipboard('38125038402')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Currency:</span>
                        <span className="text-gray-800 font-semibold">BIF (Burundian Franc)</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Bank Name:</span>
                        <span className="text-gray-800 font-semibold">Eco Bank</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="font-bold text-gray-700">Swift Code:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-gray-800 font-semibold">ECOCBIBI</span>
                          <button
                            onClick={() => copyToClipboard('ECOCBIBI')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 text-sm bg-brand-blue-50 rounded-lg p-5 mb-4 border border-brand-blue-200">
                      <p className="font-bold text-brand-gold-900 mb-2">USD Account</p>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Account Name:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-800">Cardx Academia & Travel Tours Ltd</span>
                          <button
                            onClick={() => copyToClipboard('Cardx Academia & Travel Tours Ltd')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Account Number:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-gray-800 font-bold text-lg">38125038403</span>
                          <button
                            onClick={() => copyToClipboard('38125038403')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Currency:</span>
                        <span className="text-gray-800 font-semibold">USD (US Dollars)</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-brand-blue-200">
                        <span className="font-bold text-gray-700">Bank Name:</span>
                        <span className="text-gray-800 font-semibold">Eco Bank</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="font-bold text-gray-700">Swift Code:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-gray-800 font-semibold">ECOCBIBI</span>
                          <button
                            onClick={() => copyToClipboard('ECOCBIBI')}
                            className="text-brand-blue-600 hover:text-brand-blue-700"
                            title="Copy to clipboard"
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-3 bg-brand-gold-50 p-3 rounded border border-brand-gold-200">
                      <span className="font-semibold">Payment Reference:</span> Your Full Name - Israel Pilgrimage
                    </p>
                    <p className="text-sm text-gray-700">
                      After payment, please email your proof of payment to<br />
                      <a href="mailto:info@cardxacademia.com" className="text-brand-blue-600 hover:underline font-semibold">
                        📧 info@cardxacademia.com
                      </a>
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-5 border border-brand-gold-200">
                    <h5 className="font-bold text-brand-gold-900 mb-2">Option 3: Office Payment</h5>
                    <p className="text-sm mb-4">
                      You may also bring your payment directly to one of our offices. Please come with your booking confirmation details.
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="bg-brand-gold-50 rounded-lg p-4 border border-brand-gold-100">
                        <p className="font-semibold text-brand-gold-900 mb-1">{rwanda.label}</p>
                        <p className="text-gray-700">{rwanda.address}</p>
                        <p className="text-gray-600 mt-1">{rwanda.phones.map((p) => p.display).join(' · ')}</p>
                      </div>
                      <div className="bg-brand-gold-50 rounded-lg p-4 border border-brand-gold-100">
                        <p className="font-semibold text-brand-gold-900 mb-1">{burundi.label}</p>
                        <p className="text-gray-700">{burundi.address}</p>
                        <p className="text-gray-600 mt-1">{burundi.phones.map((p) => p.display).join(' · ')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-brand-gold-300">
                  <p className="mb-2">
                    If you have any questions or need assistance, feel free to contact us at
                  </p>
                  <p>
                    <a href="mailto:info@cardxacademia.com" className="text-brand-blue-600 hover:underline font-semibold">
                      📧 info@cardxacademia.com
                    </a>
                  </p>
                </div>

                <p className="mt-6 text-lg font-medium">
                  We look forward to accompanying you on this meaningful journey.
                </p>
                <p className="text-right text-brand-gold-700 font-semibold">
                  - Cardx Academia Team
                </p>
              </div>

              {bookingId && (
                <div className="mt-6 pt-4 border-t border-brand-gold-300">
                  <p className="text-sm text-brand-gold-600">Booking ID: <strong className="font-mono">{bookingId}</strong></p>
                </div>
              )}

              <div className="mt-6">
                <Button
                  onClick={() => {
                    setSubmitted(false);
                    setBookingId(null);
                    setError(null);
                    // Reset form
                    setFormData({
                      fullName: '',
                      dateOfBirth: '',
                      gender: '',
                      nationality: '',
                      passportNumber: '',
                      passportIssueDate: '',
                      passportExpiryDate: '',
                      placeOfBirth: '',
                      email: '',
                      phone: '',
                      alternatePhone: '',
                      address: '',
                      city: '',
                      country: '',
                      churchName: '',
                      churchAddress: '',
                      pastorName: '',
                      pastorPhone: '',
                      membershipYears: '',
                      previousTravel: '',
                      medicalConditions: '',
                      dietaryRequirements: '',
                      emergencyContactName: '',
                      emergencyContactPhone: '',
                      emergencyContactRelationship: '',
                      specialRequests: '',
                      howDidYouHear: ''
                    });
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Submit Another Booking
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Error Message */}
      {error && !submitted && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-brand-sky-50 border border-brand-blue-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-2">
                <XCircle className="text-brand-gold-700" size={24} />
                <h3 className="text-lg font-semibold text-brand-gold-900">Error</h3>
              </div>
              <p className="text-brand-gold-800">{error}</p>
            </div>
          </div>
        </section>
      )}

      {/* Registration Closed */}
      {registrationClosed && !submitted && (
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-brand-sky-50 border-2 border-brand-blue-200 rounded-lg p-8 text-center">
              <XCircle className="mx-auto text-brand-gold-600 mb-4" size={48} />
              <h2 className="text-2xl font-bold text-brand-gold-800 mb-2">Registration Closed</h2>
              <p className="text-brand-gold-700 mb-6">
                The registration deadline ({PILGRIMAGE_REGISTRATION_DEADLINE_FULL}) has passed.
                We are no longer accepting new applications for this pilgrimage.
              </p>
              <Button
                onClick={() => navigate('/israel-pilgrimage-2026')}
                className="bg-brand-blue-500 hover:bg-brand-blue-600 text-white"
              >
                Back to Pilgrimage Info
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Application Form */}
      {!submitted && !registrationClosed && (
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
              <p className="text-gray-600 mb-6">
                Please fill out all required fields. Registration deadline: <strong>{PILGRIMAGE_REGISTRATION_DEADLINE_LABEL}</strong>
              </p>

              <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <User size={24} />
                  Personal Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="As it appears on passport"
                    />
                    {errors.fullName && <p className="text-brand-gold-600 text-sm mt-1">{errors.fullName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                    <Input
                      id="dateOfBirth"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                    {errors.dateOfBirth && <p className="text-brand-gold-600 text-sm mt-1">{errors.dateOfBirth}</p>}
                  </div>
                  <div>
                    <Label htmlFor="gender">Gender *</Label>
                    <Select value={formData.gender} onValueChange={(value) => handleSelectChange('gender', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.gender && <p className="text-brand-gold-600 text-sm mt-1">{errors.gender}</p>}
                  </div>
                  <div>
                    <Label htmlFor="nationality">Nationality *</Label>
                    <Input
                      id="nationality"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Your nationality"
                    />
                    {errors.nationality && <p className="text-brand-gold-600 text-sm mt-1">{errors.nationality}</p>}
                  </div>
                  <div>
                    <Label htmlFor="passportNumber">Passport Number *</Label>
                    <Input
                      id="passportNumber"
                      name="passportNumber"
                      value={formData.passportNumber}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Passport number"
                    />
                    {errors.passportNumber && <p className="text-brand-gold-600 text-sm mt-1">{errors.passportNumber}</p>}
                  </div>
                  <div>
                    <Label htmlFor="passportIssueDate">Passport Issue Date</Label>
                    <Input
                      id="passportIssueDate"
                      name="passportIssueDate"
                      type="date"
                      value={formData.passportIssueDate}
                      onChange={handleChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="passportExpiryDate">Passport Expiry Date *</Label>
                    <Input
                      id="passportExpiryDate"
                      name="passportExpiryDate"
                      type="date"
                      value={formData.passportExpiryDate}
                      onChange={handleChange}
                      required
                      className="mt-1"
                    />
                    {errors.passportExpiryDate && <p className="text-brand-gold-600 text-sm mt-1">{errors.passportExpiryDate}</p>}
                  </div>
                  <div>
                    <Label htmlFor="placeOfBirth">Place of Birth</Label>
                    <Input
                      id="placeOfBirth"
                      name="placeOfBirth"
                      value={formData.placeOfBirth}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="City, Country"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <Phone size={24} />
                  Contact Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <p className="text-brand-gold-600 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="+250 788 123 456"
                    />
                    {errors.phone && <p className="text-brand-gold-600 text-sm mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <Label htmlFor="alternatePhone">Alternate Phone</Label>
                    <Input
                      id="alternatePhone"
                      name="alternatePhone"
                      value={formData.alternatePhone}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Your city"
                    />
                    {errors.city && <p className="text-brand-gold-600 text-sm mt-1">{errors.city}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Full Address *</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Street address"
                    />
                    {errors.address && <p className="text-brand-gold-600 text-sm mt-1">{errors.address}</p>}
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Your country"
                    />
                    {errors.country && <p className="text-brand-gold-600 text-sm mt-1">{errors.country}</p>}
                  </div>
                </div>
              </div>

              {/* Church Information (Optional) */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <Building2 size={24} />
                  Church Information (Optional)
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="churchName">Church Name</Label>
                    <Input
                      id="churchName"
                      name="churchName"
                      value={formData.churchName}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Your church name (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="membershipYears">Years of Membership</Label>
                    <Input
                      id="membershipYears"
                      name="membershipYears"
                      type="number"
                      value={formData.membershipYears}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Number of years (optional)"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="churchAddress">Church Address</Label>
                    <Input
                      id="churchAddress"
                      name="churchAddress"
                      value={formData.churchAddress}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Church location (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pastorName">Pastor Name</Label>
                    <Input
                      id="pastorName"
                      name="pastorName"
                      value={formData.pastorName}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Pastor's name (optional)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="pastorPhone">Pastor Phone</Label>
                    <Input
                      id="pastorPhone"
                      name="pastorPhone"
                      value={formData.pastorPhone}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Pastor's phone (optional)"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Note: Registration is done at the CardX Academia & Travel Tours office in Kigali or our Bujumbura branch. Church information is optional.
                </p>
              </div>

              {/* Travel & Medical Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <FileText size={24} />
                  Additional Information
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="previousTravel">Previous Travel Experience</Label>
                    <Textarea
                      id="previousTravel"
                      name="previousTravel"
                      value={formData.previousTravel}
                      onChange={handleChange}
                      rows={3}
                      className="mt-1"
                      placeholder="List countries visited (if any)"
                    />
                  </div>
                  <div>
                    <Label htmlFor="medicalConditions">Medical Conditions</Label>
                    <Textarea
                      id="medicalConditions"
                      name="medicalConditions"
                      value={formData.medicalConditions}
                      onChange={handleChange}
                      rows={3}
                      className="mt-1"
                      placeholder="Any medical conditions we should know about"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dietaryRequirements">Dietary Requirements</Label>
                    <Input
                      id="dietaryRequirements"
                      name="dietaryRequirements"
                      value={formData.dietaryRequirements}
                      onChange={handleChange}
                      className="mt-1"
                      placeholder="Vegetarian, allergies, etc."
                    />
                  </div>
                  <div>
                    <Label htmlFor="howDidYouHear">How did you hear about this pilgrimage?</Label>
                    <Select value={formData.howDidYouHear} onValueChange={(value) => handleSelectChange('howDidYouHear', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="church">Church</SelectItem>
                        <SelectItem value="friend">Friend/Family</SelectItem>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="social-media">Social Media</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="specialRequests">Special Requests or Comments</Label>
                    <Textarea
                      id="specialRequests"
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleChange}
                      rows={4}
                      className="mt-1"
                      placeholder="Any special requests or additional information"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                  <Phone size={24} />
                  Emergency Contact
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="emergencyContactName">Emergency Contact Name *</Label>
                    <Input
                      id="emergencyContactName"
                      name="emergencyContactName"
                      value={formData.emergencyContactName}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Full name"
                    />
                    {errors.emergencyContactName && <p className="text-brand-gold-600 text-sm mt-1">{errors.emergencyContactName}</p>}
                  </div>
                  <div>
                    <Label htmlFor="emergencyContactPhone">Emergency Contact Phone *</Label>
                    <Input
                      id="emergencyContactPhone"
                      name="emergencyContactPhone"
                      value={formData.emergencyContactPhone}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="+250 788 123 456"
                    />
                    {errors.emergencyContactPhone && <p className="text-brand-gold-600 text-sm mt-1">{errors.emergencyContactPhone}</p>}
                  </div>
                  <div>
                    <Label htmlFor="emergencyContactRelationship">Relationship *</Label>
                    <Input
                      id="emergencyContactRelationship"
                      name="emergencyContactRelationship"
                      value={formData.emergencyContactRelationship}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder="Spouse, Parent, Sibling, etc."
                    />
                    {errors.emergencyContactRelationship && <p className="text-brand-gold-600 text-sm mt-1">{errors.emergencyContactRelationship}</p>}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand-blue-500 hover:bg-brand-blue-600 text-white font-semibold py-6 text-lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      <Send size={20} className="mr-2" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>
      )}

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default IsraelTourApplicationPage;
